import { useEffect, useRef, useState } from "react";

type Props = {
  onChange: (dataUrl: string | null) => void;
};

function canvasHasInk(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 0; i < data.length; i += 16) {
    const alpha = data[i + 3];
    if (alpha === 0) continue;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    if (r < 248 || g < 248 || b < 248) return true;
  }
  return false;
}

export function NdaSignPad({ onChange }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasInkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = 140 * dpr;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
  }, []);

  const pos = (e: React.PointerEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const emit = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasInkRef.current || !canvasHasInk(canvas)) {
      hasInkRef.current = false;
      setHasInk(false);
      onChange(null);
      return;
    }
    onChange(canvas.toDataURL("image/png"));
  };

  const endStroke = () => {
    drawing.current = false;
    emit();
  };

  return (
    <div className="stack">
      <label htmlFor="nda-sign">
        Draw signature <span className="muted">(required)</span>
      </label>
      <canvas
        id="nda-sign"
        ref={canvasRef}
        className={`sign-pad${hasInk ? " sign-pad--signed" : ""}`}
        aria-label="Signature pad"
        aria-required="true"
        onPointerDown={(e) => {
          drawing.current = true;
          const ctx = canvasRef.current!.getContext("2d")!;
          const p = pos(e);
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const ctx = canvasRef.current!.getContext("2d")!;
          const p = pos(e);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
          if (!hasInkRef.current) {
            hasInkRef.current = true;
            setHasInk(true);
          }
        }}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
      />
      {hasInk ? (
        <button
          type="button"
          className="ghost"
          style={{ alignSelf: "flex-start" }}
          onClick={() => {
            const canvas = canvasRef.current!;
            const ctx = canvas.getContext("2d")!;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            hasInkRef.current = false;
            setHasInk(false);
            onChange(null);
          }}
        >
          Clear signature
        </button>
      ) : null}
      <style>{`
        .sign-pad {
          width: 100%;
          height: 140px;
          border: 1px dashed var(--ds-border);
          border-radius: 6px;
          background: #fff;
          touch-action: none;
        }
        .sign-pad--signed {
          border-style: solid;
          border-color: var(--ds-accent);
        }
      `}</style>
    </div>
  );
}
